import { useEffect, useRef } from 'react';

interface UseWizardBrowserBackOptions {
  /** Step courant du wizard (1-indexé). */
  currentStep: number;
  /** Action de retour vers le step précédent. */
  goToPreviousStep: () => void;
  /**
   * Active la protection bouton retour (sentinelle popstate).
   * À passer dynamiquement, typiquement `currentStep > 1` pour ne pas
   * gêner l'utilisateur tant qu'il est au premier step.
   * Default: true.
   */
  enabled?: boolean;
  /**
   * Active aussi l'alerte navigateur sur fermeture/refresh (beforeunload).
   * Mettre à false pendant un submit en cours pour ne pas déclencher
   * une seconde alerte lors de la redirection post-création.
   * Default: true.
   */
  beforeUnloadEnabled?: boolean;
}

/**
 * Protection du bouton retour navigateur dans un wizard multi-step.
 *
 * Comportement (quand `enabled === true`) :
 * - Au mount, push une "sentinelle" dans l'historique (sans changer l'URL).
 * - Sur popstate (back navigateur, swipe trackpad, mouse back, Cmd+[, Alt+←, etc.) :
 *   `goToPreviousStep` + re-pousse une nouvelle sentinelle pour piéger le prochain back.
 * - Au unmount → retire la sentinelle si elle est encore en haut de la pile.
 * - En complément, intercepte beforeunload pour avertir avant fermeture/refresh.
 *
 * Quand `enabled === false`, le hook ne fait absolument rien : pas de sentinelle,
 * pas de listener, pas de beforeunload. Comportement navigateur natif.
 *
 * Pattern StrictMode-safe : les cleanups sont différés via setTimeout(0) et
 * annulés au remount immédiat (cycle mount → cleanup → mount du dev StrictMode),
 * pour éviter qu'un `history.back()` parasite ne déclenche un popstate après
 * la ré-attache du listener.
 *
 * Le pattern ne change jamais l'URL → aucun conflit avec react-router.
 */
export function useWizardBrowserBack({
  currentStep,
  goToPreviousStep,
  enabled = true,
  beforeUnloadEnabled = true,
}: UseWizardBrowserBackOptions): void {
  // Refs synchronisées à chaque rerender — le listener popstate les lit via .current
  // sans avoir à se ré-attacher (sinon on perdrait la sentinelle à chaque rerender).
  const currentStepRef = useRef(currentStep);
  const goToPreviousStepRef = useRef(goToPreviousStep);

  useEffect(() => {
    currentStepRef.current = currentStep;
    goToPreviousStepRef.current = goToPreviousStep;
  });

  // Timer de cleanup différé (StrictMode-safe : annulé au remount immédiat).
  // Concerne uniquement le history.back() de la sentinelle popstate, qui
  // dispatche un événement asynchrone susceptible d'être catché par le
  // listener du remount StrictMode.
  const popstateCleanupTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // ----- Sentinelle popstate -----
  useEffect(() => {
    if (!enabled) return;

    // Annuler tout cleanup différé d'un mount précédent (StrictMode remount).
    clearTimeout(popstateCleanupTimerRef.current);

    // Ne pousser la sentinelle que si elle n'est pas déjà présente
    // (cas du remount StrictMode où le cleanup différé a été annulé).
    if (window.history.state?.wizardSentinel !== true) {
      window.history.pushState({ wizardSentinel: true }, '');
    }

    const handlePopState = () => {
      // Le listener n'est attaché que si enabled === true, donc currentStep > 1
      // (l'appelant passe `enabled: currentStep > 1`). On retourne au step précédent
      // et on re-pose une sentinelle pour piéger le prochain back.
      goToPreviousStepRef.current();
      if (window.history.state?.wizardSentinel !== true) {
        window.history.pushState({ wizardSentinel: true }, '');
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Cleanup différé : si remount immédiat (StrictMode), il sera annulé
      // par le clearTimeout en haut de l'effect du nouveau mount.
      // Seul un VRAI unmount aboutira à l'exécution du history.back().
      popstateCleanupTimerRef.current = setTimeout(() => {
        if (window.history.state?.wizardSentinel) {
          window.history.back();
        }
      }, 0);
    };
  }, [enabled]);

  // ----- Avertissement fermeture/refresh -----
  useEffect(() => {
    if (!enabled || !beforeUnloadEnabled) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome exige une string vide pour déclencher l'alerte standard.
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled, beforeUnloadEnabled]);
}
